import {Box, Typography} from '@mui/material';
import './SelectAnalyzers.scss';
import ArrButton from '../../../../common/arr-button/ArrButton';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SelectAnalyzersTable from './select-analyzers-table/SelectAnalyzerTables';
import AnalyzerDetailsFlyout from './analyzer-details-flyout/AnalyzerDetailsFlyout';
import {useEffect, useMemo, useState} from 'react';
import NonEditableFieldFlyout from './non-editable-field-flyout/NonEditableFieldFlyout';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {setSelectedMetadataFieldNames} from '../../../../../redux/slice/createCustomIndexSlice';
import {
  useCustomIndexFields,
  useGetAllCustomIndexAnalyzers,
  useGetAnalyzers,
} from '../../../../../hooks/ingestions/useIngestions';

type CustomIndexFieldResponseItem = {
  FieldId?: number;
  FieldID?: number;
  FieldName?: string;
  AnalyzerId?: number;
  AnalyzerKey?: string;
  IsDefault?: boolean;
  IsEditable?: boolean;
  fieldId?: number;
  fieldID?: number;
  fieldName?: string;
  analyzerID?: number;
  analyzerKey?: string;
  isDefault?: boolean;
  isEditable?: boolean;
  FieldType?: string;
  fieldType?: string;
  AvailableForCustomIndex?: boolean;
  availableForCustomIndex?: boolean;
};

type AnalyzerResponseItem = {
  AnalyzerId?: number;
  AnalyzerID?: number;
  AnalyzerName?: string;
  AnalyzerKey?: string;
  analyzerID?: number;
  analyzerId?: number;
  analyzerName?: string;
  analyzerKey?: string;
  AvailableForCustomIndex?: boolean;
  availableForCustomIndex?: boolean;
  id?: number;
  name?: string;
  key?: string;
};

type NonEditableFieldItem = {
  fieldId: number;
  fieldName: string;
  fieldType: string;
};

type AnalyzerDisplayItem = {
  analyzerKey: string;
  analyzerName: string;
};

const getAnalyzers = (response: unknown): AnalyzerResponseItem[] => {
  if (Array.isArray(response)) {
    return response as AnalyzerResponseItem[];
  }

  if (
    response &&
    typeof response === 'object' &&
    Array.isArray((response as {analyzers?: unknown}).analyzers)
  ) {
    return (response as {analyzers: AnalyzerResponseItem[]}).analyzers;
  }

  return [];
};

const getAnalyzerId = (analyzer: AnalyzerResponseItem) =>
  analyzer.AnalyzerId ??
  analyzer.AnalyzerID ??
  analyzer.analyzerID ??
  analyzer.analyzerId ??
  analyzer.id;

const getAnalyzerKey = (analyzer: AnalyzerResponseItem) =>
  analyzer.AnalyzerKey ?? analyzer.analyzerKey ?? analyzer.key ?? '';

const getAnalyzerName = (analyzer: AnalyzerResponseItem) =>
  analyzer.AnalyzerName ??
  analyzer.analyzerName ??
  analyzer.name ??
  getAnalyzerKey(analyzer);

const isAvailableForCustomIndex = (analyzer: AnalyzerResponseItem) =>
  analyzer.AvailableForCustomIndex ?? analyzer.availableForCustomIndex ?? false;

const SelectAnalyzers: React.FC = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {data: analyzerResponse} = useGetAllCustomIndexAnalyzers();
  const {data: analyzerDetailsResponse} = useGetAnalyzers();
  const {data: customIndexFields} = useCustomIndexFields();

  const sortedAnalyzerResponse = useMemo(() => {
    const analyzers = getAnalyzers(analyzerResponse);
    const analyzerDetails = getAnalyzers(analyzerDetailsResponse);
    const availableForCustomIndexById = new Map<number, boolean>(
      analyzerDetails.flatMap((analyzer) => {
        const analyzerId = getAnalyzerId(analyzer);
        return analyzerId === undefined
          ? []
          : [[analyzerId, isAvailableForCustomIndex(analyzer)] as const];
      })
    );

    return [...analyzers]
      .sort((a: AnalyzerResponseItem, b: AnalyzerResponseItem) => {
        return getAnalyzerName(a).localeCompare(getAnalyzerName(b));
      })
      .map((analyzer: AnalyzerResponseItem) => ({
        ...analyzer,
        customAnalyzer:
          availableForCustomIndexById.get(getAnalyzerId(analyzer)) ?? false,
      }));
  }, [analyzerResponse, analyzerDetailsResponse]);

  const sortedAnalyzerDetailsResponse = useMemo(() => {
    const analyzers = getAnalyzers(analyzerDetailsResponse);
    const dropdownAnalyzerIds = new Set(
      sortedAnalyzerResponse
        .filter((analyzer) => analyzer.customAnalyzer)
        .map(getAnalyzerId)
        .filter((analyzerId): analyzerId is number => analyzerId !== undefined)
    );
    const prioritizedAnalyzerName = 'consilio standard text';
    return analyzers
      .filter((analyzer) => {
        const analyzerId = getAnalyzerId(analyzer);
        return analyzerId !== undefined && dropdownAnalyzerIds.has(analyzerId);
      })
      .sort((a: AnalyzerResponseItem, b: AnalyzerResponseItem) => {
        const nameA = getAnalyzerName(a).toLowerCase();
        const nameB = getAnalyzerName(b).toLowerCase();
        const isPrioritizedAnalyzerA = nameA === prioritizedAnalyzerName;
        const isPrioritizedAnalyzerB = nameB === prioritizedAnalyzerName;
        if (isPrioritizedAnalyzerA && !isPrioritizedAnalyzerB) {
          return -1;
        }
        if (!isPrioritizedAnalyzerA && isPrioritizedAnalyzerB) {
          return 1;
        }
        return nameA.localeCompare(nameB);
      });
  }, [analyzerDetailsResponse, sortedAnalyzerResponse]);

  const {isReadOnlyView, selectedMetadataFieldNames, createCustomIndex} =
    useSelector((state: RootState) => state.indexManagement);

  const [isAnalyzerDetailsFlyoutOpen, setIsAnalyzerDetailsFlyoutOpen] =
    useState<boolean>(false);
  const [isNonEditableFieldsFlyoutOpen, setIsNonEditableFieldsFlyoutOpen] =
    useState<boolean>(false);

  const catalogFieldsById = useMemo(() => {
    const fieldMap = new Map<number, CustomIndexFieldResponseItem>();
    if (!Array.isArray(customIndexFields)) {
      return fieldMap;
    }

    (customIndexFields as CustomIndexFieldResponseItem[]).forEach((field) => {
      const fieldId =
        field.FieldId ?? field.FieldID ?? field.fieldId ?? field.fieldID;
      if (fieldId !== undefined) {
        fieldMap.set(Number(fieldId), field);
      }
    });

    return fieldMap;
  }, [customIndexFields]);

  const selectedFieldsWithFieldType = useMemo(() => {
    return selectedMetadataFieldNames.map((field) => {
      const catalogField = catalogFieldsById.get(Number(field.fieldId));
      if (!catalogField || field.fieldType !== undefined) {
        return field;
      }

      return {
        ...field,
        fieldType: catalogField.FieldType ?? catalogField.fieldType,
      };
    });
  }, [catalogFieldsById, selectedMetadataFieldNames]);

  const analyzerAvailabilityById = useMemo(
    () =>
      new Map<number, boolean>(
        getAnalyzers(analyzerDetailsResponse).flatMap((analyzer) => {
          const analyzerId = getAnalyzerId(analyzer);
          return analyzerId === undefined
            ? []
            : [[analyzerId, isAvailableForCustomIndex(analyzer)] as const];
        })
      ),
    [analyzerDetailsResponse]
  );

  const selectedAnalyzerIdByFieldId = useMemo(
    () =>
      new Map(
        createCustomIndex.fields.map((field) => [
          field.fieldId,
          field.analyzerId,
        ])
      ),
    [createCustomIndex.fields]
  );

  const nonEditableFields: NonEditableFieldItem[] = useMemo(() => {
    return selectedFieldsWithFieldType
      .filter(
        (field) =>
          !analyzerAvailabilityById.get(
            selectedAnalyzerIdByFieldId.get(field.fieldId)
          )
      )
      .map((field) => ({
        fieldId: field.fieldId,
        fieldName: field.fieldName,
        fieldType: field.fieldType ?? '',
      }));
  }, [
    analyzerAvailabilityById,
    selectedAnalyzerIdByFieldId,
    selectedFieldsWithFieldType,
  ]);

  const analyzerById = useMemo(() => {
    return getAnalyzers(analyzerResponse).reduce((analyzerMap, analyzer) => {
      const analyzerId = getAnalyzerId(analyzer);
      if (analyzerId !== undefined) {
        analyzerMap.set(analyzerId, {
          analyzerKey: getAnalyzerKey(analyzer),
          analyzerName: getAnalyzerName(analyzer),
        });
      }
      return analyzerMap;
    }, new Map<number, AnalyzerDisplayItem>());
  }, [analyzerResponse]);

  useEffect(() => {
    if (!selectedMetadataFieldNames.length) {
      return;
    }
    const updatedSelectedMetadataFieldNames = selectedMetadataFieldNames.map(
      (field) => {
        const selectedField = createCustomIndex.fields.find(
          (item) => item.fieldId === field.fieldId
        );
        const analyzerDetails =
          selectedField?.analyzerId !== undefined
            ? analyzerById.get(selectedField.analyzerId)
            : undefined;
        return {
          ...field,
          analyzerKey:
            analyzerDetails?.analyzerKey ?? selectedField?.analyzerKey ?? '',
          analyzerName: analyzerDetails?.analyzerName ?? field.analyzerName,
        };
      }
    );
    const hasAnalyzerDetailsChanged = updatedSelectedMetadataFieldNames.some(
      (field, index) =>
        field.analyzerKey !== selectedMetadataFieldNames[index]?.analyzerKey ||
        field.analyzerName !== selectedMetadataFieldNames[index]?.analyzerName
    );
    if (hasAnalyzerDetailsChanged) {
      dispatch(
        setSelectedMetadataFieldNames(updatedSelectedMetadataFieldNames)
      );
    }
  }, [
    analyzerById,
    createCustomIndex.fields,
    dispatch,
    selectedMetadataFieldNames,
  ]);

  return (
    <Box className="select-analyzers-container">
      <Box className="select-analyzers">
        <Typography
          tabIndex={0}
          aria-label="select-analyzer-heading"
          className="select-analyzers__heading typo-text subhead2_size"
          data-testid={`select-analyzers-heading`}
        >
          {t('indexManagement.customIndex.selectAnalyzer.heading')}
        </Typography>
        <Box className="select-analyzers__content">
          <Box className="select-analyzers__header">
            <Box className="select-analyzers__header-text">
              <Typography
                tabIndex={0}
                className="typo-text subhead3_size"
                data-testid={`select-analyzers-sub-heading`}
              >
                {t('indexManagement.customIndex.selectAnalyzer.subHeading')}
              </Typography>
              <Typography
                tabIndex={0}
                className="select-analyzers__description typo-text body2_size"
                data-testid={`select-analyzers-description`}
              >
                {t('indexManagement.customIndex.selectAnalyzer.description')}
              </Typography>
            </Box>
            <ArrButton
              size="medium"
              dataTestId="analyzer-details-flyout-btn"
              aria-label="Open Analyzer Details"
              onClick={() => setIsAnalyzerDetailsFlyoutOpen(true)}
              endIcon={<KeyboardArrowRightIcon fontSize="medium" />}
            >
              {isReadOnlyView
                ? t('indexManagement.customIndex.readOnly.viewAnalyzers')
                : t(
                    'indexManagement.customIndex.selectAnalyzer.analyzerDetailsButton'
                  )}
            </ArrButton>
          </Box>
          <SelectAnalyzersTable
            analyzerResponse={sortedAnalyzerResponse}
            selectedMetadataFieldNames={selectedFieldsWithFieldType}
            editableOnly={!isReadOnlyView}
          />
          {!isReadOnlyView && (
            <ArrButton
              size="medium"
              variant="text"
              dataTestId="non-editable-flyout-btn"
              className="non-editable-button"
              aria-label="View non-editable fields"
              onClick={() => setIsNonEditableFieldsFlyoutOpen(true)}
              endIcon={<KeyboardArrowRightIcon fontSize="medium" />}
            >
              {t(
                'indexManagement.customIndex.selectAnalyzer.viewNonEditableFied',
                {
                  count: nonEditableFields.length,
                }
              )}
            </ArrButton>
          )}
        </Box>
        <AnalyzerDetailsFlyout
          isOpen={isAnalyzerDetailsFlyoutOpen}
          analyzerResponse={sortedAnalyzerDetailsResponse}
          onClose={() => setIsAnalyzerDetailsFlyoutOpen(false)}
        />
        <NonEditableFieldFlyout
          isOpen={isNonEditableFieldsFlyoutOpen}
          nonEditableFields={nonEditableFields}
          onClose={() => setIsNonEditableFieldsFlyoutOpen(false)}
        />
      </Box>
    </Box>
  );
};

export default SelectAnalyzers;
